/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import LoadingIcon from "assets/Icons/LoadingIcon";
import cn from "classnames";
import { FC, MouseEvent } from "react";
import { FontAwesomeIcon, IMG, Loader, Stack } from "..";
import s from "./DocumentCard.module.css";
type ICardVariant = "pref" | "doc" | "downloadable" | "allergy";
type ICardState = "success" | "failed" | "loading" | "missing";

type Props = {
  media?: string;
  title: string;
  note?: string;
  variant?: ICardVariant;
  state: ICardState;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: Function;
  onIconClick: Function;
};
const DocumentCard: FC<Props> = ({
  media,
  title,
  note,
  variant,
  state,
  isDisabled,
  isLoading,
  onClick,
  onIconClick,
}) => {
  const docs = variant === "doc" || variant === "downloadable";
  let successState = state === "success" && (!isDisabled || docs);
  let failState = state === "failed" && (!isDisabled || docs);
  let loadingState = state === "loading" && (!isDisabled || docs);
  const disabledFlag = isDisabled || state === "loading";
  let svgFill;
  let svgStroke;
  let circleStroke;
  const handleIconClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onIconClick) onIconClick(e);
  };
  if (docs) {
    svgFill = "none";
    svgStroke = "#4DCE60";
    circleStroke = "#4DCE60";
  }
  return (
    <div
      className="w-full h-full"
      role={onClick ? "button" : ""}
      onClick={onClick ? () => onClick() : undefined}
    >
      <div
        className={cn(
          s.root,
          variant && s[variant],
          { [s.active]: successState }, // success
          { [s.loading]: loadingState }, // loading
          isDisabled && s.disabled,
          { "cursor-default": disabledFlag },
          docs && failState && s.failed, //failed
          state === "missing" && "border -border--theme-colors-danger-default"
        )}
      >
        <div>
          {docs && <IMG src={media} alt={title} className="w-full h-full" />}
        </div>
        <div className={cn("relative")}>
          <Stack className="text-start">
            <h5>{title}</h5>
            {note !== "" && <p>{note}</p>}
          </Stack>
          {successState && variant === "doc" && (
            <span className="-text--theme-colors-success-default">
              <FontAwesomeIcon name="fa-circle-check" size="2x" />
            </span>
          )}
          {successState && variant === "downloadable" && (
            <div
              className="h-16 justify-center items-center flex"
              role="button"
              onClick={handleIconClick}
            >
              <FontAwesomeIcon name="fa-cloud-arrow-down" size="lg" />
            </div>
          )}
          {docs && failState && (
            <div
              className="h-16 justify-center items-center flex -text--theme-colors-danger-default"
              role="button"
              onClick={handleIconClick}
            >
              <FontAwesomeIcon name="fa-triangle-exclamation" size="2x" />
            </div>
          )}
          {docs && loadingState && (
            <div className="end-0">
              <LoadingIcon />
            </div>
          )}
          {!disabledFlag && isLoading && <Loader className="end-0.5 top-2" />}
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
